/*
 * Wave 9 (docs/implemented-offline9.md): what the server would refuse, checked on the device before something made with no
 * connection is kept for sending. No browser APIs, so server/test-offline9.js runs it in Node and sends the same bodies to
 * the server: what this refuses, the server refuses too. The server still checks everything again on replay (a customer
 * made inactive meanwhile, a unique custom field another record took, the stock): this only spares the person a refusal
 * hours later for something the device could have said at once.
 *
 *   checkOffline(method, path, body, look, tr)   null, or the first problem in words (`tr` is the screens' t(), passed in
 *                                                so this file needs no browser; the texts are in every language)
 *   look   the copies kept on this device (offlineCache.ts): the active customers and items, the purchases accounts, the
 *          projects, the custom fields of each module, an invoice's balance. A list not kept is not checked against.
 *
 * Checked like the server: customers (name, emails, GSTIN with its state and PAN, PAN, payment terms, tags), vendors (name,
 * emails, GSTIN, PAN, PIN code, payment terms), items (name, rate, tax rate, HSN/SAC), invoices and estimates (customer,
 * lines with item or name, quantities, rates, dates), expenses (date, amount, accounts, GST rate), time (project, date,
 * hours), payments (amount within what is due, date) and, on a new record, its custom fields (required, number, date,
 * choice, email, web address, yes or no).
 */

// The screens' t(), as a type only: this file never loads the dictionaries; the caller passes t() in as `tr`, and the build's
// language check reads every tr("…") here as a screen text that needs its translations.
import type { t as tr } from "../i18n";

type Tr = typeof tr;
type Rec = Record<string, unknown>;
export type Field = { id: string; label: string; type: string; options?: string[] | null; is_required?: boolean; status?: string };
export type Lookups = {
  customers?: { id: string; status?: string }[] | null;
  items?: { id: string; status?: string }[] | null;
  accounts?: { id: string; group?: string }[] | null;
  projects?: { id: string; status?: string }[] | null;
  fields?: Field[] | null;
  /** The invoice a payment is recorded on, as kept: what is still due and its date. */
  invoice?: { balance?: string | null; issue_date?: string | null } | null;
  today?: string;
};

const GSTIN = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN = /^[A-Z]{5}\d{4}[A-Z]$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ITEM_GST = 40;
const PURCHASE_GST = [0, 0.25, 3, 5, 12, 18, 28];
const LEGACY_ACCOUNT = /^(exp_[a-z_]+|pt_[a-z]+|cogs)$/;

const has = (b: Rec, k: string) => b[k] !== undefined;
const blank = (v: unknown) => v === undefined || v === null || (typeof v === "string" && !v.trim());
const str = (v: unknown) => (typeof v === "string" ? v.trim() : v === null || v === undefined ? "" : String(v));
const num = (v: unknown) => (typeof v === "number" ? v : Number(str(v).replace(/,/g, "")));
const isDay = (v: unknown) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)) && new Date(v).toISOString().slice(0, 10) === v;
const list = (v: unknown) => (Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : []).map(str).filter(Boolean);

/** The record kind a path writes, and whether it makes a new one. */
function kindOf(method: string, path: string): { kind: string; create: boolean; id?: string } | null {
  const p = path.split("?")[0];
  const rules: [RegExp, string][] = [
    [/^\/admin\/customers(?:\/([^/]+))?$/, "customer"],
    [/^\/admin\/purchases\/vendors(?:\/([^/]+))?$/, "vendor"],
    [/^\/admin\/items(?:\/([^/]+))?$/, "item"],
    [/^\/admin\/purchases\/expenses(?:\/([^/]+))?$/, "expense"],
    [/^\/admin\/sales\/(?:invoices|estimates)(?:\/([^/]+))?$/, "doc"],
    [/^\/admin\/projects\/time-entries(?:\/((?!approve$|reject$)[^/]+))?$/, "time"],
    [/^\/admin\/sales\/invoices\/([^/]+)\/payments$/, "payment"],
    [/^\/admin\/offline\/issue$/, "issue"],
  ];
  for (const [re, kind] of rules) {
    const m = re.exec(p);
    if (!m) continue;
    if (kind === "payment" || kind === "issue") return method === "POST" ? { kind, create: true, id: m[1] } : null;
    return { kind, create: method === "POST" && !m[1], id: m[1] };
  }
  return null;
}

/** The custom fields module of a record kind (server/customfields.js FIELD_MODULES). */
const FIELD_MODULE: Record<string, string> = { customer: "customers", vendor: "vendors", item: "items", expense: "expenses", doc: "invoices", issue: "invoices" };
export const fieldModuleOf = (method: string, path: string) => {
  const k = kindOf(method, path);
  if (!k?.create) return null;
  if (k.kind === "doc" && !/\/invoices/.test(path)) return null;
  return FIELD_MODULE[k.kind] ?? null;
};

function customFields(b: Rec, fields: Field[] | null | undefined, tr: Tr): string | null {
  if (!fields?.length) return null;
  const values = (b.custom_fields && typeof b.custom_fields === "object" ? b.custom_fields : {}) as Rec;
  for (const f of fields.filter((x) => x.status === undefined || x.status === "active")) {
    const v = values[f.id];
    if (blank(v) || (f.type === "checkbox" && f.is_required && (v === false || v === "false"))) {
      if (f.is_required) return tr("{missing} is required.", { missing: f.label });
      continue;
    }
    const s = str(v);
    if (f.type === "number" && (typeof v === "boolean" || !Number.isFinite(Number(s)))) return tr("{name} must be a number.", { name: f.label });
    if (f.type === "date" && !isDay(s.slice(0, 10))) return tr("{name} must be a date.", { name: f.label });
    if (f.type === "dropdown" && !(f.options ?? []).includes(s)) return tr("{name}: choose one of the listed options.", { name: f.label });
    if (f.type === "email" && !EMAIL.test(s)) return tr("{name}: the email address is not valid.", { name: f.label });
    if (f.type === "url" && !/^https?:\/\/[^\s]+\.[^\s]+/.test(s)) return tr("{name} must be a full web address, like https://example.com.", { name: f.label });
    if (f.type === "checkbox" && !["true", "false"].includes(s)) return tr("{name} must be yes or no.", { name: f.label });
    if (f.type === "text" && s.length > 500) return tr("{name} is too long.", { name: f.label });
  }
  return null;
}

function party(b: Rec, create: boolean, nameKey: "display_name" | "name", tr: Tr, vendor: boolean): string | null {
  if ((create || has(b, nameKey)) && blank(b[nameKey])) return tr("Enter the name.");
  if (str(b[nameKey]).length > 160) return tr("The name is too long.");
  for (const e of [b.email, ...list(b.other_emails)]) if (!blank(e) && !EMAIL.test(str(e))) return tr("The email address {email} is not valid.", { email: str(e) });
  const gstin = str(b.gstin).replace(/\s/g, "").toUpperCase();
  const pan = str(b.pan).toUpperCase();
  if (gstin && !GSTIN.test(gstin)) return tr("The GSTIN is not valid: 15 characters, like 27ABCDE1234F1Z5.");
  if (gstin && !blank(b.state_code) && gstin.slice(0, 2) !== str(b.state_code)) return tr("The GSTIN begins with a different state code than the state chosen.");
  if (pan && !PAN.test(pan)) return tr("PAN is not valid (10 characters, like ABCDE1234F).");
  if (gstin && pan && gstin.slice(2, 12) !== pan) return tr("The PAN does not match the one inside the GSTIN.");
  if (!vendor && b.gst_treatment === "business_gst" && !gstin) return tr("A registered business needs its GSTIN.");
  if (!vendor && has(b, "gst_treatment") && b.gst_treatment !== "business_gst" && gstin) return tr("A GSTIN can only be kept for a registered business.");
  if (vendor && b.gst_treatment === "registered" && !gstin) return tr("A registered vendor needs a GSTIN.");
  if (has(b, "payment_terms_days")) {
    const n = num(b.payment_terms_days);
    if (!Number.isInteger(n) || n < 0 || n > 365) return tr("Payment terms is a whole number of days, 0 to 365.");
  }
  if (list(b.tags).length > (vendor ? 10 : 20)) return tr("Too many tags.");
  if (vendor && !blank(b.pincode) && !/^\d{6}$/.test(str(b.pincode))) return tr("The PIN code is 6 digits.");
  return null;
}

function item(b: Rec, create: boolean, tr: Tr): string | null {
  if ((create || has(b, "name")) && blank(b.name)) return tr("Enter the name.");
  if (has(b, "rate") && !blank(b.rate) && !(num(b.rate) >= 0 && num(b.rate) <= 1e9)) return tr("The rate must be an amount of zero or more.");
  if (has(b, "tax_rate") && !blank(b.tax_rate) && !(num(b.tax_rate) >= 0 && num(b.tax_rate) <= ITEM_GST)) return tr("Tax rate must be a number from 0 to {RATE_MAX}.", { RATE_MAX: ITEM_GST });
  if (!blank(b.hsn_sac) && !/^\d{4,8}$/.test(str(b.hsn_sac))) return tr("HSN/SAC code is 4 to 8 digits.");
  return null;
}

function doc(b: Rec, create: boolean, look: Lookups, tr: Tr): string | null {
  if ((create || has(b, "customer_id")) && blank(b.customer_id)) return tr("Choose the customer.");
  const c = look.customers && !blank(b.customer_id) ? look.customers.find((x) => x.id === b.customer_id) : undefined;
  if (look.customers && !blank(b.customer_id) && create && !c) return tr("That customer is not among the active customers kept on this device. Choose another, or make the invoice when you are back online.");
  for (const k of ["issue_date", "due_date", "expiry_date"]) if (!blank(b[k]) && !isDay(b[k])) return tr("The date is not valid.");
  if (!blank(b.due_date) && !blank(b.issue_date) && str(b.due_date) < str(b.issue_date)) return tr("The due date can't be before the invoice date.");
  if (create || has(b, "lines")) {
    const lines = Array.isArray(b.lines) ? (b.lines as Rec[]) : [];
    if (!lines.length) return tr("Add at least one line.");
    if (lines.length > 100) return tr("A document can have at most 100 lines.");
    for (const [i, l] of lines.entries()) {
      const at = tr("Line {n}", { n: i + 1 });
      if (blank(l.item_id) && blank(l.name)) return tr("{at}: enter what is being sold, or choose an item.", { at });
      if (!blank(l.item_id) && look.items && !look.items.some((x) => x.id === l.item_id)) return tr("{at}: that item is not among the active items kept on this device.", { at });
      const q = num(l.quantity);
      if (!(q > 0 && q <= 1e6) || Math.abs(q * 1000 - Math.round(q * 1000)) > 1e-6) return tr("{at}: the quantity must be more than 0, with at most 3 decimals.", { at });
      if (!blank(l.rate) && !/^\d+(\.\d{1,2})?$/.test(str(l.rate).replace(/,/g, ""))) return tr("{at}: the rate must be an amount in rupees, with at most 2 decimals.", { at });
      if (blank(l.item_id) && blank(l.rate)) return tr("{at}: enter the rate.", { at });
      if (!blank(l.hsn_sac) && !/^\d{4,8}$/.test(str(l.hsn_sac))) return tr("{at}: HSN/SAC is 4 to 8 digits.", { at });
    }
  }
  return null;
}

function expense(b: Rec, create: boolean, look: Lookups, tr: Tr): string | null {
  if ((create || has(b, "date")) && !isDay(b.date)) return tr("The date is not valid.");
  if (create || has(b, "amount")) {
    const s = str(b.amount).replace(/[,\s₹]/g, "");
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return tr("Enter the amount, with at most 2 decimals.");
    if (Number(s) <= 0) return tr("The amount must be more than zero.");
  }
  // The older purchases ids (exp_other, pt_bank…) are mapped by the server to their chart accounts (server/ledger-chart.js).
  const known = (v: unknown) => LEGACY_ACCOUNT.test(str(v)) || Boolean(look.accounts?.some((a) => a.id === v));
  if (look.accounts && !blank(b.account_id) && !known(b.account_id)) return tr("That expense account is not among the accounts kept on this device.");
  if (look.accounts && !blank(b.paid_through_id) && !known(b.paid_through_id)) return tr("That account it was paid through is not among the accounts kept on this device.");
  if (has(b, "gst_rate") && !blank(b.gst_rate) && !PURCHASE_GST.includes(num(b.gst_rate))) return tr("The GST rate must be one of {rates}.", { rates: PURCHASE_GST.join(", ") });
  return null;
}

/** "1:30", "1.5" or minutes, as minutes (server/projects.js). */
function minutes(v: unknown): number | null {
  if (typeof v === "number") return v;
  const m = /^(\d{1,3})(?::(\d{2})|\.(\d{1,2}))?$/.exec(str(v));
  if (!m || (m[2] && Number(m[2]) > 59)) return null;
  return Number(m[1]) * 60 + (m[2] ? Number(m[2]) : m[3] ? Math.round(Number(`0.${m[3]}`) * 60) : 0);
}

function time(b: Rec, create: boolean, look: Lookups, tr: Tr): string | null {
  if ((create || has(b, "project_id")) && blank(b.project_id)) return tr("Choose the project.");
  if (create && look.projects && !look.projects.some((p) => p.id === b.project_id && (p.status === undefined || p.status === "active"))) return tr("That project is not among the active projects kept on this device.");
  if ((create || has(b, "date")) && !isDay(b.date)) return tr("The date is not valid.");
  if (create || has(b, "hours") || has(b, "minutes")) {
    const m = has(b, "minutes") ? num(b.minutes) : minutes(b.hours);
    if (m === null || !Number.isFinite(m)) return tr("Enter the time spent, as hours (1.5) or hours:minutes (1:30).");
    if (m < 1 || m > 1440) return tr("A time entry is from 1 minute to 24 hours.");
  }
  return null;
}

function payment(b: Rec, look: Lookups, tr: Tr): string | null {
  const s = str(b.amount).replace(/[,\s₹]/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(s) || Number(s) < 0.01) return tr("Enter the amount received, at least ₹0.01.");
  if (!blank(b.received_on) && !isDay(b.received_on)) return tr("The date is not valid.");
  if (look.today && !blank(b.received_on) && str(b.received_on) > look.today) return tr("The payment date can't be in the future.");
  if (look.invoice?.issue_date && !blank(b.received_on) && str(b.received_on) < look.invoice.issue_date) return tr("A payment can't be dated before the invoice.");
  const due = look.invoice?.balance ? num(look.invoice.balance) : NaN;
  if (Number.isFinite(due) && Number(s) > due && !b.excess_as_advance) return tr("Only {balance} is still due on this invoice.", { balance: due.toFixed(2) });
  return null;
}

/** The first thing the server would refuse in this write, or null. */
export function checkOffline(method: string, path: string, body: unknown, look: Lookups, tr: Tr): string | null {
  const k = kindOf(method, path);
  if (!k || !body || typeof body !== "object" || Array.isArray(body)) return null;
  const b = body as Rec;
  const own = (() => {
    switch (k.kind) {
      case "customer":
        return party(b, k.create, "display_name", tr, false);
      case "vendor":
        return party(b, k.create, "name", tr, true);
      case "item":
        return item(b, k.create, tr);
      case "doc":
        return doc(b, k.create, look, tr);
      case "issue":
        return b.draft && typeof b.draft === "object" ? doc(b.draft as Rec, true, look, tr) ?? customFields(b.draft as Rec, look.fields, tr) : null;
      case "expense":
        return expense(b, k.create, look, tr);
      case "time":
        return time(b, k.create, look, tr);
      case "payment":
        return payment(b, look, tr);
      default:
        return null;
    }
  })();
  if (own) return own;
  return k.create && k.kind !== "issue" && fieldModuleOf(method, path) ? customFields(b, look.fields, tr) : null;
}
